import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { getShuffledVerbs } from '../utils/loadingVerbs';

interface LoadingExperienceProps {
  compact?: boolean;
  subtitle?: string;
}

export const LoadingExperience: React.FC<LoadingExperienceProps> = ({
  compact = false,
  subtitle = 'Server is crunching decades of squirrel lore in the background',
}) => {
  const verbs = useMemo(() => getShuffledVerbs(), []);
  const [verbIndex, setVerbIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [recentVerbs, setRecentVerbs] = useState<string[]>([]);
  const [elapsedTenths, setElapsedTenths] = useState(0);

  const currentVerb = verbs[verbIndex % verbs.length];

  // Elapsed timer for extra telemetry feel
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTenths(prev => prev + 1);
    }, 100);
    return () => clearInterval(timer);
  }, []);

  // Typewriter + cycle to next verb
  useEffect(() => {
    let charIdx = 0;
    setTypedText('');

    const typeInterval = setInterval(() => {
      charIdx += 1;
      setTypedText(currentVerb.slice(0, charIdx));
      if (charIdx >= currentVerb.length) {
        clearInterval(typeInterval);
      }
    }, 28);

    const nextVerbTimeout = setTimeout(
      () => {
        setRecentVerbs(prev => [currentVerb, ...prev.slice(0, 2)]);
        setVerbIndex(prev => (prev + 1) % verbs.length);
      },
      Math.max(1900, currentVerb.length * 32 + 950)
    );

    return () => {
      clearInterval(typeInterval);
      clearTimeout(nextVerbTimeout);
    };
  }, [currentVerb, verbs.length]);

  if (compact) {
    return (
      <Flex
        align='center'
        justify='center'
        py={8}
        px={4}
        w='100%'
        direction='column'
        gap={3}
      >
        <HStack
          px={4}
          py={2.5}
          borderRadius='full'
          bg='rgba(20, 24, 38, 0.8)'
          border='1px solid rgba(168, 85, 247, 0.3)'
          boxShadow='0 8px 32px rgba(139, 92, 246, 0.2)'
          backdropFilter='blur(16px)'
          gap={3}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid rgba(236, 72, 153, 0.25)',
              borderTopColor: '#ec4899',
              borderRightColor: '#8b5cf6',
            }}
          />
          <Text
            fontSize='sm'
            fontWeight='600'
            bgGradient='to-r'
            gradientFrom='pink.300'
            gradientVia='purple.300'
            gradientTo='cyan.300'
            bgClip='text'
            fontFamily='mono'
          >
            {typedText}
            <motion.span
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.7, repeat: Infinity }}
              style={{ color: '#ec4899', marginLeft: 2 }}
            >
              ▋
            </motion.span>
          </Text>
        </HStack>
      </Flex>
    );
  }

  return (
    <Flex
      w='100%'
      minH={{ base: '60vh', md: '68vh' }}
      align='center'
      justify='center'
      px={4}
      py={10}
      position='relative'
      overflow='hidden'
    >
      {/* Ambient pulsing glow orbs */}
      <motion.div
        animate={{
          scale: [1, 1.25, 1],
          opacity: [0.22, 0.38, 0.22],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          width: '340px',
          height: '340px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(236,72,153,0.35) 0%, rgba(139,92,246,0.18) 50%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      <Box
        maxW='560px'
        w='100%'
        p={{ base: 6, md: 9 }}
        borderRadius='2xl'
        bg='rgba(15, 18, 28, 0.75)'
        backdropFilter='blur(24px)'
        border='1px solid rgba(255, 255, 255, 0.09)'
        boxShadow='0 24px 64px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.12)'
        position='relative'
        zIndex={1}
      >
        <VStack gap={6} align='center'>
          {/* Multi-ring Quantum Orbital Spinner */}
          <Box
            position='relative'
            w='96px'
            h='96px'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            {/* Outer ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px dashed rgba(139, 92, 246, 0.45)',
              }}
            />
            {/* Middle neon ring */}
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
              style={{
                position: 'absolute',
                inset: '10px',
                borderRadius: '50%',
                border: '3px solid transparent',
                borderTopColor: '#ec4899',
                borderLeftColor: '#8b5cf6',
                borderBottomColor: '#06b6d4',
                filter: 'drop-shadow(0 0 10px rgba(236, 72, 153, 0.5))',
              }}
            />
            {/* Inner fast ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.3, repeat: Infinity, ease: 'linear' }}
              style={{
                position: 'absolute',
                inset: '22px',
                borderRadius: '50%',
                border: '2px solid transparent',
                borderTopColor: '#22d3ee',
                borderRightColor: '#a855f7',
              }}
            />
            {/* Center pulsing core */}
            <motion.div
              animate={{
                scale: [0.9, 1.15, 0.9],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)',
                boxShadow: '0 0 20px rgba(236, 72, 153, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
              }}
            >
              🐿️
            </motion.div>
          </Box>

          {/* Status telemetry pill */}
          <HStack
            px={3}
            py={1}
            borderRadius='full'
            bg='rgba(139, 92, 246, 0.14)'
            border='1px solid rgba(139, 92, 246, 0.3)'
            gap={2}
          >
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: '#22d3ee',
                boxShadow: '0 0 8px #22d3ee',
              }}
            />
            <Text
              fontSize='xs'
              fontWeight='600'
              letterSpacing='0.08em'
              textTransform='uppercase'
              color='purple.200'
            >
              Quantum Squirrel Engine
            </Text>
            <Text fontSize='xs' color='cyan.300' fontFamily='mono'>
              {(elapsedTenths / 10).toFixed(1)}s
            </Text>
          </HStack>

          {/* Animated Ridiculous Verb Display */}
          <Box
            minH='68px'
            display='flex'
            alignItems='center'
            justifyContent='center'
            w='100%'
            textAlign='center'
            px={2}
          >
            <AnimatePresence mode='wait'>
              <motion.div
                key={verbIndex}
                initial={{ opacity: 0, y: 12, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -12, filter: 'blur(6px)' }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ width: '100%' }}
              >
                <Text
                  fontSize={{ base: 'lg', md: '2xl' }}
                  fontWeight='800'
                  lineHeight='1.3'
                  letterSpacing='-0.02em'
                  bgGradient='to-r'
                  gradientFrom='white'
                  gradientVia='pink.200'
                  gradientTo='cyan.200'
                  bgClip='text'
                >
                  {typedText}
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.65, repeat: Infinity }}
                    style={{
                      color: '#ec4899',
                      marginLeft: '3px',
                      fontWeight: 400,
                    }}
                  >
                    |
                  </motion.span>
                </Text>
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Animated Equalizer / Shimmer Progress Bar */}
          <Box
            w='100%'
            h='6px'
            borderRadius='full'
            bg='rgba(255,255,255,0.06)'
            overflow='hidden'
            position='relative'
          >
            <motion.div
              animate={{ x: ['-100%', '200%'] }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                width: '50%',
                height: '100%',
                borderRadius: '999px',
                background:
                  'linear-gradient(90deg, transparent, #ec4899, #8b5cf6, #06b6d4, transparent)',
              }}
            />
          </Box>

          {/* Completed ridiculous steps trail */}
          {recentVerbs.length > 0 && (
            <VStack gap={1} w='100%' pt={1}>
              {recentVerbs.map((rv, idx) => (
                <motion.div
                  key={`${rv}-${idx}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: Math.max(0.2, 0.55 - idx * 0.18), y: 0 }}
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  <Text
                    fontSize='xs'
                    color='gray.400'
                    fontFamily='mono'
                    truncate
                  >
                    ✓ {rv.replace(/\.+$/, '')} — done
                  </Text>
                </motion.div>
              ))}
            </VStack>
          )}

          <Text fontSize='xs' color='gray.400' textAlign='center'>
            {subtitle}
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};
